import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2, CircleAlert, Clock3, Link2, Mail, MapPin, Phone, PlayCircle, UserRound, XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { formatDollars } from '@/components/deal/mockDealData';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';
import ProposalDeliveryEstimateCard from '@/components/deal/ProposalDeliveryEstimateCard';
import useDealFlow from '@/hooks/useDealFlow';

function normalizeTextValue(value, fallback = 'Not provided') {
  if (value == null) return fallback;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || fallback;
  return fallback;
}

function getSummaryValue(summary = null, keys = [], fallback = 'Not provided') {
  if (!summary || typeof summary !== 'object') return fallback;

  for (const key of keys) {
    const value = summary[key];
    if (value != null && !(typeof value === 'string' && value.trim() === '')) {
      return value;
    }
  }

  return fallback;
}

function formatCurrencyValue(value) {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return formatDollars(numeric);
  }
  return normalizeTextValue(value);
}

function PublicProposalState({ icon: Icon, title, description }) {
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12 sm:px-6">
      <div className="mx-auto flex min-h-[70vh] max-w-xl items-center justify-center">
        <Card className="w-full border-slate-200 shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
              <Icon className="h-6 w-6 text-slate-600" />
            </div>
            <h1 className="mt-5 text-2xl font-semibold text-slate-950">{title}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function PublicProposal() {
  const { token } = useParams();
  const deal = useDealFlow();
  const { loadPublicProposal } = deal;
  const [publicProposal, setPublicProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setLoadError(null);
    setPreviewFailed(false);

    loadPublicProposal(token).then((response) => {
      if (!cancelled) {
        setPublicProposal(response);
      }
    }).catch((error) => {
      if (!cancelled) {
        setLoadError(error);
      }
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [loadPublicProposal, token]);

  const creative = publicProposal?.selectedCreative || null;
  const creativeVideoUrl = creative?.shareUrl || creative?.previewUrl || '';
  const targetingLabel = useMemo(() => normalizeTextValue(
    getSummaryValue(publicProposal?.targetingSummary, ['label', 'summary', 'target_label']),
  ), [publicProposal?.targetingSummary]);
  const flightLabel = useMemo(() => normalizeTextValue(
    getSummaryValue(publicProposal?.flightSummary, ['label', 'summary']),
  ), [publicProposal?.flightSummary]);
  const pricingLabel = useMemo(() => {
    const value = getSummaryValue(publicProposal?.pricingSummary, [
      'sell_price',
      'sellPrice',
      'price',
      'total_price',
      'totalPrice',
    ]);
    return formatCurrencyValue(value);
  }, [publicProposal?.pricingSummary]);

  if (loading) {
    return (
      <PublicProposalState
        icon={Clock3}
        title="Loading proposal"
        description="Please wait while we load this shared proposal."
      />
    );
  }

  if (loadError) {
    return (
      <PublicProposalState
        icon={CircleAlert}
        title="Proposal unavailable"
        description={loadError.message || 'This proposal could not be loaded right now.'}
      />
    );
  }

  if (!publicProposal) {
    return (
      <PublicProposalState
        icon={CircleAlert}
        title="Proposal unavailable"
        description="This proposal could not be loaded right now."
      />
    );
  }

  if (publicProposal.state === 'invalid') {
    return <PublicProposalState icon={Link2} title="Proposal link invalid" description={publicProposal.description} />;
  }

  if (publicProposal.state === 'expired') {
    return <PublicProposalState icon={Clock3} title="Proposal link expired" description={publicProposal.description} />;
  }

  if (publicProposal.state === 'revoked') {
    return <PublicProposalState icon={XCircle} title="Proposal link revoked" description={publicProposal.description} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.16em] text-slate-500">CornerCast proposal</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              {publicProposal.packageName}
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Prepared for {publicProposal.business?.name || publicProposal.advertiserName || 'your business'}
              {publicProposal.proposalVersion ? ` · Proposal v${publicProposal.proposalVersion}` : ''}
            </p>
          </div>
          <Badge variant="outline" className="w-fit rounded-full border-slate-200 bg-white text-slate-700">
            {String(publicProposal.status || 'shared').replace(/_/g, ' ')}
          </Badge>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.35fr_0.95fr]">
          <div className="space-y-6">
            <Card className="overflow-hidden border-slate-200 pt-0 shadow-sm">
              <CardContent className="p-0">
                <div className="aspect-video bg-slate-950 relative">
                  {creativeVideoUrl && !previewFailed ? (
                    <video
                      src={creativeVideoUrl}
                      poster={creative?.thumbnailUrl || undefined}
                      controls
                      playsInline
                      className="h-full w-full object-cover"
                      onError={() => setPreviewFailed(true)}
                    />
                  ) : creative?.thumbnailUrl && !previewFailed ? (
                    <img
                      src={creative.thumbnailUrl}
                      alt={creative.displayName || 'Creative preview'}
                      className="h-full w-full object-cover"
                      onError={() => setPreviewFailed(true)}
                    />
                  ) : (
                    <CreativePreviewFallback label="Creative preview unavailable" />
                  )}
                  {!creativeVideoUrl ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-sm">
                        <PlayCircle className="h-7 w-7 text-slate-900" />
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="space-y-5 p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium uppercase tracking-[0.16em] text-slate-500">Proposal summary</p>
                      <h2 className="mt-1 text-2xl font-semibold text-slate-950">
                        {publicProposal.business?.name || publicProposal.advertiserName || 'Business'}
                      </h2>
                    </div>
                    {creative?.displayName ? (
                      <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                        {creative.displayName}
                      </Badge>
                    ) : null}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Investment</p>
                      <p className="mt-1 text-lg font-semibold text-slate-950">{pricingLabel}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Run time</p>
                      <p className="mt-1 text-lg font-semibold text-slate-950">{flightLabel}</p>
                    </div>
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Where it shows</p>
                      <p className="mt-1 text-lg font-semibold text-slate-950">{targetingLabel}</p>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="text-sm font-semibold text-slate-950">Message</h3>
                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm leading-6 whitespace-pre-wrap text-slate-700">
                      {publicProposal.pitchMessage || '(No additional message from your seller.)'}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="text-sm font-semibold text-slate-950">What happens next</h3>
                    <ol className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
                      <li>1. Review this proposal and the sample ad above.</li>
                      <li>2. Reach out with any changes — your contact details are on the right.</li>
                      <li>3. When you're ready, accept and complete payment to launch.</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>

            <ProposalDeliveryEstimateCard deliveryEstimate={publicProposal.deliveryEstimate} />
          </div>

          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="space-y-5 p-5">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-600" />
                  <h3 className="text-base font-semibold text-slate-950">Business details</h3>
                </div>
                <div className="space-y-3 text-sm text-slate-700">
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Business</p>
                    <p className="mt-1 font-medium text-slate-950">{publicProposal.business?.name || publicProposal.advertiserName || 'Business'}</p>
                  </div>
                  {publicProposal.business?.address ? (
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <span>{publicProposal.business.address}</span>
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="space-y-5 p-5">
                <h3 className="text-base font-semibold text-slate-950">Offer details</h3>
                <div className="space-y-4 text-sm text-slate-700">
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Investment</p>
                    <p className="mt-1 font-medium text-slate-950">{pricingLabel}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Where it shows</p>
                    <p className="mt-1">{targetingLabel}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Run time</p>
                    <p className="mt-1">{flightLabel}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {publicProposal.sellerContact ? (
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="space-y-4 p-5">
                  <div className="flex items-center gap-2">
                    <UserRound className="h-4 w-4 text-slate-600" />
                    <h3 className="text-base font-semibold text-slate-950">Seller contact</h3>
                  </div>
                  <div className="space-y-2 text-sm text-slate-700">
                    <p className="font-medium text-slate-950">{publicProposal.sellerContact.name || 'Your CornerCast contact'}</p>
                    {publicProposal.sellerContact.title ? (
                      <p>{publicProposal.sellerContact.title}</p>
                    ) : null}
                    {publicProposal.sellerContact.company ? (
                      <p>{publicProposal.sellerContact.company}</p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {publicProposal.sellerContact.email ? (
                      <Button asChild variant="outline" className="rounded-xl bg-white">
                        <a href={`mailto:${publicProposal.sellerContact.email}`}>
                          <Mail className="h-4 w-4" />
                          Email seller
                        </a>
                      </Button>
                    ) : null}
                    {publicProposal.sellerContact.phone ? (
                      <Button asChild variant="outline" className="rounded-xl bg-white">
                        <a href={`tel:${publicProposal.sellerContact.phone}`}>
                          <Phone className="h-4 w-4" />
                          Call seller
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
